import React, { useRef, useEffect } from "react";
import { useController } from "react-hook-form";
import { GdkZipCode } from "@gdk/zip-code";
import { v4 } from "uuid";
import { classes } from "../../util/style-utils";

/**
 * ZipCode component that renders a ZIP code input field using GDK ZipCode component.
 * Integrates with React Hook Form for form state management and validation.
 * 
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for managing form state
 * @param {string} [props.name="zipCode"] - The name attribute for the form field
 * @param {string} [props.label="ZIP Code"] - The label text displayed above the input field
 * @param {string} [props.placeholder="12345"] - The placeholder text shown in the input field
 * @param {Function} [props.onBlur] - Optional callback function triggered when the input loses focus
 * @returns {JSX.Element} A ZIP code input form field component
 */
export default function ZipCode({ control, name = "zipCode", label = "ZIP Code", placeholder = "12345", onBlur }) {
    /** @type {React.MutableRefObject<GdkZipCode>} Reference to the GDK ZipCode instance */
    const zipCode = useRef();

    /** @type {React.MutableRefObject<string>} Reference to the unique container element ID */
    const containerIdRef = useRef(`zipcode-container-${v4()}`);

    /** @type {React.MutableRefObject<string>} Reference to the unique input element ID */
    const inputIdRef = useRef(`zipcode-input-${v4()}`);

    const {
        field: { onChange, onBlur: fieldOnBlur, value, ref },
        fieldState: { error }
    } = useController({
        name,
        control,
        defaultValue: ""
    });

    useEffect(() => {
        zipCode.current = new GdkZipCode({
            content: `#${containerIdRef.current}`,
            inputPlaceHolder: placeholder,
            callBackOnZipChange: () => {
                onChange(zipCode.current.getValue());
            },
        });

        // Set initial value if provided
        if (value) {
            zipCode.current.setValue(value);
        }
    }, [name, placeholder]);

    useEffect(() => {
        if (zipCode.current && zipCode.current.getValue() !== (value || "")) {
            zipCode.current.setValue(value || "");
        }
    }, [value]);

    /**
     * Handles the blur event for the ZIP code input field.
     * Triggers both the field's onBlur handler and any custom onBlur callback.
     */
    const handleBlur = () => {
        fieldOnBlur();
        if (onBlur) {
            onBlur();
        }
    };

    return (
        <div id={containerIdRef.current} className={classes("form-field", error && "form-field--error")}>
            <label htmlFor={inputIdRef.current} className="text">
                {label}
            </label>
            <div className="zip-code-form">
                <input
                    id={inputIdRef.current}
                    className="zip-code-input"
                    type="tel"
                    size="15"
                    ref={ref}
                    onBlur={handleBlur}
                />
            </div>
            {error && (
                <span className="form-message">
                    {error.message}
                </span>
            )}
        </div>
    );
}